import { RecommendationFilters } from '../../types/index';
import { BaseRecommender } from './baseRecommender';
import { ContentBasedRecommender } from './contentBasedRecommender';
import { CollaborativeRecommender } from './collaborativeRecommender';
import { PopularRecommender } from './popularRecommender';
import { GameProcessingUtils } from './utils/gameProcessingUtils';
import { RecommendationDataService } from './services/recommendationDataService';
import { RECOMMENDATION_CONFIG } from './index';

/**
 * Recomendador híbrido que combina resultados de diferentes algoritmos
 * para gerar recomendações diversificadas e de alta qualidade
 */
export class HybridRecommender extends BaseRecommender {
  private contentRecommender: ContentBasedRecommender;
  private collaborativeRecommender: CollaborativeRecommender;
  private popularRecommender: PopularRecommender;

  constructor() {
    super();
    this.contentRecommender = new ContentBasedRecommender();
    this.collaborativeRecommender = new CollaborativeRecommender();
    this.popularRecommender = new PopularRecommender();
  }

  /**
   * Obtém recomendações combinando diferentes algoritmos
   */
  public async getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<any[]> {
    try {
      // Validar IDs de exclusão usando serviço centralizado
      const validExcludeIds = await this.getCompleteExcludeIds(userId, excludeGameIds);
      
      // Aplicar filtros inteligentes baseados nas preferências do usuário
      const enhancedFilters = await this.applyDefaultFilters(userId, filters);
      
      // Determinar proporção de cada tipo de recomendação usando a config
      const weights = RECOMMENDATION_CONFIG.hybridStrategyWeights;
      const totalWeight = weights.contentBased + weights.collaborative + weights.popular;

      // Normalizar pesos se não somarem 1 (ou perto disso) para evitar distorções no count
      // Esta é uma forma simples, idealmente a soma dos counts deveria ser igual ao 'count' original.
      // A lógica de Math.ceil pode fazer com que a soma dos counts exceda o 'count' original.
      // Uma abordagem mais precisa seria calcular counts proporcionais e ajustar o último.
      const contentBasedCount = Math.ceil(count * (weights.contentBased / totalWeight));
      const collaborativeCount = Math.ceil(count * (weights.collaborative / totalWeight));
      // O popularCount pode ser o restante para garantir que a soma seja 'count', ou calculado proporcionalmente também.
      // Para simplificar e manter a proporção, calculamos todos e depois fatiamos o resultado final.
      const popularCount = Math.ceil(count * (weights.popular / totalWeight));
      
      console.log(`Híbrido: buscando ${contentBasedCount} (conteúdo), ${collaborativeCount} (colab), ${popularCount} (popular)`);
      
      // Buscar recomendações baseadas em conteúdo
      let contentBasedRecommendations: any[] = [];
      try {
        contentBasedRecommendations = await this.contentRecommender.getRecommendations(
          userId,
          contentBasedCount,
          validExcludeIds,
          enhancedFilters
        );
      } catch (error) {
        console.error('Erro ao obter recomendações baseadas em conteúdo:', error);
      }
      
      // IDs já recomendados para evitar duplicação
      const recommendedIds = new Set(contentBasedRecommendations.map(game => game.id));
      const combinedExcludeIds = [...validExcludeIds, ...Array.from(recommendedIds)];
      
      console.log(`Híbrido: buscando ${collaborativeCount} recomendações colaborativas`);
      
      // Buscar recomendações colaborativas
      let collaborativeRecommendations: any[] = [];
      try {
        collaborativeRecommendations = await this.collaborativeRecommender.getRecommendations(
          userId,
          collaborativeCount,
          combinedExcludeIds,
          enhancedFilters
        );
      } catch (error) {
        console.error('Erro ao obter recomendações colaborativas:', error);
      }
      
      // Atualizar IDs já recomendados
      collaborativeRecommendations.forEach(game => recommendedIds.add(game.id));
      const finalExcludeIds = [...validExcludeIds, ...Array.from(recommendedIds)];
      
      console.log(`Híbrido: buscando ${popularCount} recomendações populares`);
      
      // Buscar recomendações populares
      let popularRecommendations: any[] = [];
      try {
        popularRecommendations = await this.popularRecommender.getRecommendations(
          userId,
          popularCount,
          finalExcludeIds,
          enhancedFilters
        );
      } catch (error) {
        console.error('Erro ao obter recomendações populares:', error);
      }
      
      // Combinar resultados
      const allRecommendations = [
        ...contentBasedRecommendations,
        ...collaborativeRecommendations,
        ...popularRecommendations
      ];
      
      // Remover duplicatas (garantia extra)
      const uniqueRecommendations = GameProcessingUtils.removeDuplicateGames(allRecommendations);
      
      // Se temos recomendações suficientes, retornar
      if (uniqueRecommendations.length >= count) {
        return GameProcessingUtils.shuffleArray(uniqueRecommendations).slice(0, count);
      }
      
      // Se não temos recomendações suficientes, usar fallback
      const remainingCount = count - uniqueRecommendations.length;
      console.warn(`Híbrido: poucas recomendações (${uniqueRecommendations.length}), buscando ${remainingCount} extras via fallback`);
      
      const fallbackRecommendations = await this.getFallbackRecommendations(
        remainingCount, 
        [...validExcludeIds, ...uniqueRecommendations.map(game => game.id)]
      );
      
      // Retornar combinação final
      return GameProcessingUtils.shuffleArray([
        ...uniqueRecommendations,
        ...fallbackRecommendations
      ]).slice(0, count);
    } catch (error) {
      console.error('Erro fatal no recomendador híbrido:', error);
      return this.getFallbackRecommendations(count, excludeGameIds);
    }
  }
} 